"use client"

import { useLayoutEffect, useRef } from "react"

/**
 * Header margin-top 动态调整 Hook
 * 根据 Header 实际高度动态为 main 设置 margin-top，避免内容被固定头部遮挡
 */
export function useHeaderMarginTop(
  headerRef: React.RefObject<HTMLElement | null>,
  transparentMode: boolean,
  initialHeaderMarginTopDesktop: number,
  initialHeaderMarginTopMobile: number
) {
  const originalMainMarginTopRef = useRef<string | null>(null)

  useLayoutEffect(() => {
    const mains = document.querySelectorAll("main")
    const mainEl = mains.length > 0 ? (mains[0] as HTMLElement) : null

    if (mains.length > 1) {
      const msg = `页面不允许存在多个 <main> 元素，当前发现 ${mains.length} 个，请检查 layout/page 避免嵌套 main。`
      console.error(msg)
      throw new Error(`不允许多个 main 嵌套，当前发现 ${mains.length} 个 main。`)
    }

    if (mainEl && originalMainMarginTopRef.current === null) {
      // 仅记录内联样式，避免把来自样式表的值当作可恢复的“原始值”
      originalMainMarginTopRef.current = mainEl.style.marginTop
    }

    // 仅在非透明模式下为 main 添加顶部间距，避免固定 Header 遮挡内容
    if (transparentMode) {
      if (mainEl && originalMainMarginTopRef.current !== null) {
        mainEl.style.marginTop = originalMainMarginTopRef.current ?? ""
      }
      return
    }

    const headerEl = headerRef.current

    if (!headerEl || !mainEl) {
      return
    }

    const getTargetMargin = () => {
      const isMobile = window.matchMedia("(max-width: 768px)").matches
      const presetMargin = isMobile ? initialHeaderMarginTopMobile : initialHeaderMarginTopDesktop
      return presetMargin > 0 ? presetMargin : headerEl.getBoundingClientRect().height
    }

    const getCurrentComputedMargin = () => {
      const currentMarginTop = window.getComputedStyle(mainEl).marginTop
      return parseFloat(currentMarginTop || "0") || 0
    }

    const applyMargin = (value: number) => {
      const current = getCurrentComputedMargin()
      if (Math.abs(current - value) > 1) {
        mainEl.style.marginTop = `${value}px`
      }
    }

    const updateMainMargin = () => {
      const headerHeightWithSafeOffset = headerEl.getBoundingClientRect().height
      applyMargin(headerHeightWithSafeOffset)
    }

    // 先使用服务端预估高度（桌面/移动端），避免首屏闪烁
    applyMargin(getTargetMargin())

    // 再用真实高度修正，且仅在差异明显时更新，减少同帧重复写入
    updateMainMargin()

    const resizeObserver = new ResizeObserver(updateMainMargin)
    resizeObserver.observe(headerEl)
    window.addEventListener("resize", updateMainMargin)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener("resize", updateMainMargin)
      mainEl.style.marginTop = originalMainMarginTopRef.current ?? ""
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHeaderMarginTopDesktop, initialHeaderMarginTopMobile, transparentMode])
}
